import React, { useEffect, useState } from 'react'
import { makeStyles } from '@mui/styles'
import { CircularProgress, Grid, Typography } from '@mui/material'

import { useModal, withModal } from 'utils/providers/ModalProvider'
import { useAuth } from 'utils/providers/AuthProvider'

import modals from 'constants/modals'

import {
  BaseField,
  CloseIcon,
  PlusIcon,
  ReactSelect,
  StyledButton,
  StyledDialog,
  UPLOAD_IMAGE_TYPES,
  createUppy,
} from 'shared'

import { BaseModalComponentType } from 'components/modals/types'
import { Controller, useForm } from 'react-hook-form'
import { SupportPayload, supportValidationSchema } from 'utils/validations'
import { useMutation } from '@tanstack/react-query'
import { yupResolver } from '@hookform/resolvers/yup'
import { API } from 'api'
import { sendSupportLetterAPI } from 'api/support'
import { toast } from 'react-toastify'
import { DashboardModal } from '@uppy/react'

const useStyles = makeStyles(() => {
  return {
    dialog: {
      '& .base-dialog-header > *': {
        fontSize: '24px',
        fontWeight: '700',
        lineHeight: '24px',
      },
    },
    subtitle: {
      fontSize: '16px',
      fontWeight: '400',
      lineHeight: '20px',
      marginBottom: '12px',
    },
    textArea: {
      padding: 0,
    },
  }
})

export const themeOptions = [
  {
    value: 1,
    label: 'Request campaign extension: “I need more time to submit my review”',
  },
  {
    value: 2,
    label: 'Request campaign exemption: “I want to be removed from a campaign”',
  },
  {
    value: 3,
    label: 'Update/change shipping address',
  },
  {
    value: 4,
    label: 'Issues submitting content (photos/videos)',
  },
  {
    value: 5,
    label: 'I’d like to receive feedback on my content',
  },
  {
    value: 6,
    label: 'I want to invite a friend to Hue',
  },
  {
    value: 7,
    label: 'I’m having a bad experience with a product',
  },
  {
    value: 8,
    label: 'When will I hear back about a specific campaign?',
  },
  {
    value: 9,
    label: 'Other',
  },
]

const SupportModal = ({ isOpen, closeModal }: BaseModalComponentType) => {
  const classes = useStyles()
  const { showModal } = useModal()

  const { user } = useAuth()
  const [uppyOpened, setUppyOpened] = useState(false)

  const supportMutation = useMutation({
    mutationFn: sendSupportLetterAPI,
  })

  const { control, handleSubmit, setValue } = useForm<
    NonNullable<SupportPayload>
  >({
    resolver: yupResolver(supportValidationSchema),
    defaultValues: {
      email: '',
      topic: !user?.profile?.isVerified
        ? themeOptions[themeOptions.length - 1]
        : themeOptions[0],
      question: '',
      attachmentUrl: '',
    },
  })

  const [uppy] = useState(() => {
    const uppy = createUppy(API)

    uppy.setOptions({
      autoProceed: true,
      restrictions: {
        allowedFileTypes: UPLOAD_IMAGE_TYPES,
        maxNumberOfFiles: 1,
      },
    })

    uppy.on('complete', (data) => {
      const [file] = data.successful

      setUppyOpened(false)
      uppy.removeFile(file.id)
      setValue('attachmentUrl', decodeURIComponent(file.uploadURL))
    })

    return uppy
  })

  const submit = handleSubmit(
    async (data) => {
      try {
        await supportMutation.mutateAsync({
          ...data,
          topic: data.topic.label,
        })
        closeModal()
        showModal('supportSuccess')
      } catch (err) {
        console.error(err)
      }
    },
    (err) => {
      toast.error('Something went wrong. Please try again later')
      console.log(err)
    }
  )

  useEffect(() => {
    if (!user) return
    setValue('email', user.email)
  }, [user, setValue])

  return (
    <StyledDialog open={isOpen} onClose={closeModal} className={classes.dialog}>
      <Grid
        container
        flexWrap="nowrap"
        gap={1}
        alignItems="flex-start"
        justifyContent="space-between"
        mb={3}
      >
        <Typography fontSize="24px" fontWeight="700" lineHeight={1}>
          Hi beauty <br /> How can we help?
        </Typography>
        <CloseIcon onClick={closeModal} />
      </Grid>
      <Typography variant="subtitle1" className={classes.subtitle}>
        Write your question here, we will respond to your email as soon as
        possible
      </Typography>
      <Grid container flexDirection="column" gap={1} mb={2}>
        {user?.email ? null : (
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState }) => {
              return (
                <BaseField
                  value={field.value}
                  label="Email address"
                  error={fieldState.invalid}
                  placeholder="Type email address here"
                  helperText={fieldState.error?.message}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              )
            }}
          />
        )}
        {user?.profile?.isVerified ? (
          <Controller
            control={control}
            name="topic"
            render={({ field, fieldState }) => {
              return (
                <ReactSelect
                  name=""
                  options={themeOptions}
                  value={field.value}
                  label="Select topic"
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                  placeholder="Select theme here"
                />
              )
            }}
          />
        ) : null}
        <Controller
          control={control}
          name="question"
          render={({ field, fieldState }) => {
            return (
              <BaseField
                name=""
                value={field.value}
                label="Question"
                error={fieldState.invalid}
                placeholder="Type your question here"
                helperText={fieldState.error?.message}
                onChange={(e) => field.onChange(e.target.value)}
                multiline
                rows={2}
              />
            )
          }}
        />

        {user?.email ? (
          <>
            <Controller
              control={control}
              name="attachmentUrl"
              render={({ field }) => {
                if (field.value) {
                  return (
                    <Grid
                      mt={1}
                      width="fit-content"
                      position="relative"
                      onClick={() => {
                        if (!field.value) return
                        showModal('mediaPreview', { url: field.value })
                      }}
                    >
                      <img
                        style={{
                          width: '80px',
                          height: '80px',
                          borderRadius: '16px',
                          objectFit: 'cover',
                        }}
                        src={field.value}
                        alt=""
                      />
                      <Grid
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                          top: -8,
                          right: -8,
                          padding: '4px',
                          position: 'absolute',
                          borderRadius: '100%',
                          backgroundColor: ({ palette }) =>
                            palette.common.white,
                        }}
                      >
                        <CloseIcon
                          onClick={(e) => {
                            e.stopPropagation()
                            field.onChange('')
                          }}
                          sx={{
                            width: '16px',
                            height: '16px',
                          }}
                        />
                      </Grid>
                    </Grid>
                  )
                }
                return (
                  <Grid>
                    <Typography>Attach file</Typography>
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="center"
                      onClick={() => setUppyOpened(true)}
                      sx={{
                        height: '92px',
                        width: '100%',
                        borderRadius: '8px',
                        border: '1px dashed rgba(3, 16, 29, 0.50)',
                      }}
                    >
                      <PlusIcon />
                    </Grid>
                  </Grid>
                )
              }}
            />
            <DashboardModal
              open={uppyOpened}
              onRequestClose={() => setUppyOpened(false)}
              uppy={uppy}
              proudlyDisplayPoweredByUppy={false}
            />
          </>
        ) : null}
      </Grid>

      <Grid container flexWrap="nowrap" gap={2}>
        <StyledButton
          fullWidth
          onClick={closeModal}
          variant="contained"
          color="light"
        >
          Cancel
        </StyledButton>
        <StyledButton
          fullWidth
          onClick={submit}
          variant="contained"
          color="mint"
          disabled={supportMutation.isLoading}
          startIcon={
            supportMutation.isLoading ? <CircularProgress size="1em" /> : null
          }
        >
          Submit
        </StyledButton>
      </Grid>
    </StyledDialog>
  )
}

export default withModal(modals.supportModal)(SupportModal)
